/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { Modal } from 'antd';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { User, UserResponse } from '@/client';
import { BackButton } from '@/components/Buttons/BackButton';
import { ContentContainer } from '@/components/ContentContainer';
import { QueryWrapper } from '@/components/QueryWrapper';
import { useMessage } from '@/hooks/message';
import { useUpdateUserMutation } from '@/hooks/mutation/useUpdateUserMutation';
import { useUserByIdQuery } from '@/hooks/query/useUserByIdQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { UserDetailsHeader } from '@/pages/UserDetails/UserDetailsHeader';
import { UserDetailsSubtenantsHierarchy } from '@/pages/UserDetails/UserDetailsSubtenantsHierarchy';
import { Container } from '@/primitives/Container';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type UserDetailsPageProps = {
  userId: string;
};

export function UserDetailsPage({ userId }: UserDetailsPageProps) {
  const { t } = useMimicTranslation('users');
  const tenantID = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const [message] = useMessage();

  const navigate = useNavigate();

  const [deleteUserModalOpen, setDeleteUserModalOpen] = useState(false);

  const userQuery = useUserByIdQuery({ userId });

  const { mutate: updateUserMutation } = useUpdateUserMutation({
    onError: (_, updateUserDto) => {
      const displayName = `${updateUserDto.firstName} ${updateUserDto.lastName}`;
      message.error(t('feedback.errorOnUpdate', { user: `${displayName}` }));
    },
    onSuccess: ({ data: userUpdated }: UserResponse) => {
      message.success(t('feedback.userUpdated', { user: `${userUpdated.displayName}` }));
    },
  });

  const updateUserRemoved = (user: User, removed: boolean) => {
    updateUserMutation({ ...user, requestBody: { removed } });
  };

  const openUpdateUserModal = (user: User) => {
    navigate(`/tenants/${tenantID}/users/update/${user.id}`);
  };

  const handleRemoveUser = (user: User, removed: boolean) => {
    updateUserRemoved(user, removed);
    setDeleteUserModalOpen(false);
    navigate(`/tenants/${tenantID}/users`);
  };

  return (
    <ContentContainer width="100%">
      <Container>
        <BackButton to={`/tenants/${tenantID}/users`} label={t('back')} />
        <QueryWrapper queryResult={userQuery}>
          {(userData) => (
            <>
              <UserDetailsHeader
                user={userData.data}
                openUpdateUserModal={openUpdateUserModal}
                setDeleteUserModalOpen={setDeleteUserModalOpen}
              />
              <UserDetailsSubtenantsHierarchy tenantID={tenantID!} userId={userId} />
              <Modal
                open={deleteUserModalOpen}
                onCancel={() => setDeleteUserModalOpen(false)}
                onOk={() => handleRemoveUser(userData.data, true)}
                cancelText={t('delete.cancel')}
                okText={t('delete.confirm')}
              >
                <Container padding="sm">
                  <Title level={4}>{t('delete.title')}</Title>
                  <Text>{t('delete.description', { userName: userData.data.displayName })}</Text>
                </Container>
              </Modal>
            </>
          )}
        </QueryWrapper>
      </Container>
    </ContentContainer>
  );
}
